import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  AppBar,
  Toolbar,
  Divider,
  CircularProgress,
  Slide,
  Button,
  Collapse,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ShareIcon from "@mui/icons-material/Share";
import ContactsIcon from "@mui/icons-material/Contacts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ForumIcon from "@mui/icons-material/Forum";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

// Native Contact Picker API (Chrome Android / Edge / some Chromium desktop)
const CONTACTS_API = "contacts" in navigator && "ContactsManager" in window;

// Avatar color helper
const AVATAR_COLORS = [
  "#1976d2", "#388e3c", "#f57c00", "#7b1fa2",
  "#c62828", "#00838f", "#5d4037", "#0288d1",
];
function avatarColor(name = "") {
  const i = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[i];
}

function ContactSearch({ open, onClose, authUserId }) {
  const [searchTerm, setSearchTerm]       = useState("");
  const [allUsers, setAllUsers]           = useState([]);
  const [loading, setLoading]             = useState(false);

  const [importing, setImporting]         = useState(false);
  const [importDone, setImportDone]       = useState(false);
  const [matchedUsers, setMatchedUsers]   = useState([]);
  const [unmatchedContacts, setUnmatched] = useState([]);
  const [showManualInput, setShowManual]  = useState(false);
  const [manualInput, setManualInput]     = useState("");
  const [showAllChatio, setShowAllChatio] = useState(true);
  const [batchLoading, setBatchLoading]   = useState(false);
  const autoPrompted = useRef(false);

  const navigate = useNavigate();

  // Load all Chatio users
  useEffect(() => {
    if (!open || !authUserId) return;
    setLoading(true);
    getDocs(collection(db, "users"))
      .then((snap) => {
        const users = snap.docs
          .filter((d) => d.id !== authUserId)
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
        setAllUsers(users);
      })
      .catch((err) => console.error("loadUsers:", err))
      .finally(() => setLoading(false));
  }, [open, authUserId]);

  // Auto-trigger native Contact Picker once per session open (after users loaded)
  useEffect(() => {
    if (!open || !CONTACTS_API || autoPrompted.current || importDone || allUsers.length === 0) return;
    autoPrompted.current = true;
    const t = setTimeout(() => handleNativeImport(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allUsers, importDone]);

  // Cross-reference device contacts with Chatio users
  const matchWithChatio = useCallback(
    (contacts) => {
      const emailMap = {};
      const phoneMap = {};
      allUsers.forEach((u) => {
        if (u.email) emailMap[u.email.toLowerCase().trim()] = u;
        if (u.phone) phoneMap[u.phone.replace(/\s+/g, "")] = u;
      });

      const matched   = [];
      const unmatched = [];

      contacts.forEach(({ name, emails = [], phones = [] }) => {
        let found = null;
        for (const e of emails) {
          const hit = emailMap[e.toLowerCase().trim()];
          if (hit) { found = hit; break; }
        }
        if (!found) {
          for (const p of phones) {
            const hit = phoneMap[p.replace(/\s+/g, "")];
            if (hit) { found = hit; break; }
          }
        }
        if (found) {
          if (!matched.find((u) => u.id === found.id))
            matched.push({ ...found, _deviceName: name });
        } else {
          unmatched.push({ _deviceName: name, _email: emails[0] || "", _phone: phones[0] || "" });
        }
      });

      matched.sort((a, b) =>
        (a.displayName || a._deviceName).localeCompare(b.displayName || b._deviceName)
      );
      unmatched.sort((a, b) => a._deviceName.localeCompare(b._deviceName));
      setMatchedUsers(matched);
      setUnmatched(unmatched);
      setImportDone(true);
      setImporting(false);
    },
    [allUsers]
  );

  // Native Contact Picker API — triggers the real browser/Android contacts popup
  const handleNativeImport = useCallback(async () => {
    setImporting(true);
    try {
      // getProperties() returns which fields this device/browser supports
      const supported = await navigator.contacts.getProperties();
      const props = ["name"];
      if (supported.includes("tel"))   props.push("tel");
      if (supported.includes("email")) props.push("email");

      const raw = await navigator.contacts.select(props, { multiple: true });
      if (!raw || raw.length === 0) { setImporting(false); return; }

      const contacts = raw.map((c) => ({
        name:   (Array.isArray(c.name) ? c.name[0] : c.name) || "Sin nombre",
        emails: Array.isArray(c.email) ? c.email : [],
        phones: Array.isArray(c.tel)   ? c.tel   : [],
      }));
      matchWithChatio(contacts);
    } catch (err) {
      // AbortError = user closed the picker without selecting — not an error
      if (err.name !== "AbortError") console.error("Contacts API:", err);
      setImporting(false);
    }
  }, [matchWithChatio]);

  // Manual import (paste emails/phones) — desktop fallback
  const handleManualImport = () => {
    if (!manualInput.trim()) return;
    const lines = manualInput
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const contacts = lines.map((line) => ({
      name:   line,
      emails: line.includes("@") ? [line] : [],
      phones: !line.includes("@") ? [line] : [],
    }));
    setImporting(true);
    matchWithChatio(contacts);
    setShowManual(false);
    setManualInput("");
  };

  // Share invite
  const handleInvite = (contact) => {
    const text = `¡Hola ${contact._deviceName}! Te invito a usar Chatio: https://chatio-9ce76.web.app/`;
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(text);
    }
  };

  // Start a single chat
  const handleStartChat = (user) => {
    if (!authUserId) return;
    const chatId = [authUserId, user.id].sort().join("_");
    handleClose();
    navigate(`/direct-chat/${chatId}/${user.id}/${authUserId}`);
  };

  // Navigate to the last matched contact so all chats appear in the list
  const handleChatWithAll = () => {
    if (!authUserId || matchedUsers.length === 0) return;
    setBatchLoading(true);
    const last = matchedUsers[matchedUsers.length - 1];
    const chatId = [authUserId, last.id].sort().join("_");
    handleClose();
    navigate(`/direct-chat/${chatId}/${last.id}/${authUserId}`);
  };

  // Reset on close
  const handleClose = () => {
    setSearchTerm("");
    setAllUsers([]);
    setImportDone(false);
    setMatchedUsers([]);
    setUnmatched([]);
    setShowManual(false);
    setManualInput("");
    setShowAllChatio(true);
    setBatchLoading(false);
    autoPrompted.current = false;
    onClose();
  };

  // Search filters
  const filteredAll = useMemo(() => {
    if (!searchTerm.trim()) return allUsers;
    const t = searchTerm.toLowerCase();
    return allUsers.filter(
      (u) =>
        (u.displayName || "").toLowerCase().includes(t) ||
        (u.email || "").toLowerCase().includes(t)
    );
  }, [allUsers, searchTerm]);

  const filteredMatched = useMemo(() => {
    if (!searchTerm.trim()) return matchedUsers;
    const t = searchTerm.toLowerCase();
    return matchedUsers.filter(
      (u) =>
        (u.displayName || "").toLowerCase().includes(t) ||
        (u._deviceName || "").toLowerCase().includes(t)
    );
  }, [matchedUsers, searchTerm]);

  const filteredUnmatched = useMemo(() => {
    if (!searchTerm.trim()) return unmatchedContacts;
    const t = searchTerm.toLowerCase();
    return unmatchedContacts.filter((c) => c._deviceName.toLowerCase().includes(t));
  }, [unmatchedContacts, searchTerm]);

  const isLoading = loading || importing;

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: "background.paper",
          zIndex: 1400,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* AppBar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{ bgcolor: "white", color: "text.primary", borderBottom: "1px solid #e0e0e0" }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={handleClose} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                Nueva conversación
              </Typography>
              {!isLoading && (
                <Typography variant="caption" color="text.secondary">
                  {filteredAll.length} usuario{filteredAll.length !== 1 ? "s" : ""} en Chatio
                </Typography>
              )}
            </Box>
          </Toolbar>

          {/* Search bar */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <TextField
              fullWidth
              autoFocus
              size="small"
              placeholder="Buscar usuario o contacto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px",
                  bgcolor: "#f5f5f5",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </AppBar>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>

          {/* Import contacts banner */}
          {!importDone && (
            <Box
              sx={{
                mx: 2, mt: 2, mb: 1,
                borderRadius: "20px",
                overflow: "hidden",
                border: "1px solid #c5deff",
                boxShadow: "0 2px 12px rgba(25,118,210,0.08)",
              }}
            >
              {/* Banner header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                  px: 2.5, py: 2,
                  display: "flex", alignItems: "center", gap: 1.5,
                }}
              >
                <ContactsIcon sx={{ color: "white", fontSize: 32 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="700" color="white" lineHeight={1.2}>
                    {CONTACTS_API ? "Tus contactos en Chatio" : "Buscar contactos"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.82)" }}>
                    {CONTACTS_API
                      ? "Chatio pedirá acceso a tus contactos para encontrar quiénes ya están aquí."
                      : "Ingresá correos o teléfonos para encontrar tus contactos en Chatio."}
                  </Typography>
                </Box>
              </Box>

              {/* Banner body */}
              <Box sx={{ background: "#f4f9ff", px: 2.5, py: 2 }}>
                {importing ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                    <CircularProgress size={22} sx={{ color: "#1976d2" }} />
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Buscando tus contactos en Chatio...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {CONTACTS_API && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<ContactsIcon />}
                        onClick={handleNativeImport}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          fontWeight: 700,
                          py: 1.2,
                          background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                          boxShadow: "0 3px 10px rgba(25,118,210,0.35)",
                          "&:hover": { background: "linear-gradient(90deg, #1565c0, #1e88e5)" },
                        }}
                      >
                        Permitir acceso a contactos
                      </Button>
                    )}

                    {!showManualInput && (
                      <Button
                        variant={CONTACTS_API ? "outlined" : "contained"}
                        fullWidth
                        startIcon={<PersonAddAlt1Icon />}
                        onClick={() => setShowManual(true)}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          fontWeight: 600,
                          py: 1,
                          ...(CONTACTS_API
                            ? { color: "#1976d2", borderColor: "#1976d2", bgcolor: "white" }
                            : {
                                background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                                "&:hover": { background: "linear-gradient(90deg, #1565c0, #1e88e5)" },
                              }),
                        }}
                      >
                        {CONTACTS_API ? "Ingresar correos / teléfonos" : "Buscar por correo o teléfono"}
                      </Button>
                    )}

                    <Collapse in={showManualInput}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Ingresá correos o teléfonos separados por coma, punto y coma o línea nueva:
                        </Typography>
                        <TextField
                          multiline
                          minRows={3}
                          fullWidth
                          size="small"
                          placeholder={"ejemplo@email.com\notro@gmail.com, +54911..."}
                          value={manualInput}
                          onChange={(e) => setManualInput(e.target.value)}
                          sx={{ bgcolor: "white", borderRadius: "8px" }}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleManualImport}
                            disabled={!manualInput.trim()}
                            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, flex: 1 }}
                          >
                            Buscar en Chatio
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => { setShowManual(false); setManualInput(""); }}
                            sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}
                          >
                            Cancelar
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>

                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setImportDone(true)}
                      sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary", alignSelf: "center" }}
                    >
                      Ahora no
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Import result summary + batch action */}
          {importDone && (matchedUsers.length > 0 || unmatchedContacts.length > 0) && (
            <Box sx={{ mx: 2, mt: 2, mb: 1 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  bgcolor: "#f0faf0",
                  border: "1px solid #c8e6c9",
                  display: "flex", alignItems: "center", gap: 1,
                  mb: matchedUsers.length > 1 ? 1 : 0,
                }}
              >
                <CheckCircleIcon sx={{ color: "#43a047", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="caption" fontWeight="700" color="#2e7d32" sx={{ flex: 1 }}>
                  {matchedUsers.length} en Chatio · {unmatchedContacts.length} para invitar
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { setImportDone(false); setMatchedUsers([]); setUnmatched([]); }}
                  sx={{ textTransform: "none", fontSize: "0.72rem", color: "text.secondary", minWidth: 0, p: 0.5 }}
                >
                  Reimportar
                </Button>
              </Box>

              {/* Batch: open conversation with all matched contacts */}
              {matchedUsers.length > 1 && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    batchLoading
                      ? <CircularProgress size={18} color="inherit" />
                      : <ForumIcon />
                  }
                  disabled={batchLoading}
                  onClick={handleChatWithAll}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.2,
                    background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                    boxShadow: "0 3px 10px rgba(25,118,210,0.3)",
                    "&:hover": { background: "linear-gradient(90deg, #1565c0, #1e88e5)" },
                  }}
                >
                  Nueva conversación con todos ({matchedUsers.length})
                </Button>
              )}
            </Box>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loading && (
            <>
              {/* Contactos del dispositivo que ya usan Chatio */}
              {filteredMatched.length > 0 && (
                <>
                  <Typography
                    variant="caption" fontWeight="bold" color="primary"
                    sx={{ px: 2, pt: 2, pb: 0.5, display: "block", letterSpacing: 0.5 }}
                  >
                    TUS CONTACTOS EN CHATIO · {filteredMatched.length}
                  </Typography>
                  <List disablePadding>
                    {filteredMatched.map((user, i) => (
                      <React.Fragment key={`m-${user.id}`}>
                        <ListItemButton
                          onClick={() => handleStartChat(user)}
                          sx={{ px: 2, py: 1.2, "&:hover": { bgcolor: "#f0f4ff" } }}
                        >
                          <ListItemAvatar>
                            <Box sx={{ position: "relative" }}>
                              <Avatar
                                src={user.profileImageUrl || user.photoURL || ""}
                                sx={{ bgcolor: avatarColor(user.displayName), width: 46, height: 46 }}
                              >
                                {(user.displayName || "?")[0].toUpperCase()}
                              </Avatar>
                              <CheckCircleIcon
                                sx={{
                                  position: "absolute", bottom: -2, right: -2,
                                  fontSize: 16, color: "#43a047",
                                  bgcolor: "white", borderRadius: "50%",
                                }}
                              />
                            </Box>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight="500">
                                {user.displayName || user._deviceName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {user._deviceName !== user.displayName
                                  ? `~${user._deviceName}`
                                  : user.email || ""}
                              </Typography>
                            }
                          />
                          <Chip
                            label="En Chatio"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: "0.65rem", height: 22 }}
                          />
                        </ListItemButton>
                        {i < filteredMatched.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}

              {/* Contactos para invitar */}
              {filteredUnmatched.length > 0 && (
                <>
                  <Typography
                    variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ px: 2, pt: 2, pb: 0.5, display: "block", letterSpacing: 0.5 }}
                  >
                    INVITAR A CHATIO · {filteredUnmatched.length}
                  </Typography>
                  <List disablePadding>
                    {filteredUnmatched.map((c, i) => (
                      <React.Fragment key={`u-${i}`}>
                        <ListItem
                          sx={{ px: 2, py: 1.2 }}
                          secondaryAction={
                            <IconButton size="small" color="primary" onClick={() => handleInvite(c)}>
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: avatarColor(c._deviceName), width: 46, height: 46 }}>
                              {(c._deviceName || "?")[0].toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" color="text.secondary" fontWeight="400">
                                {c._deviceName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.disabled" noWrap>
                                {c._email || c._phone || ""}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {i < filteredUnmatched.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}

              {/* Todos los usuarios de Chatio (colapsable) */}
              {filteredAll.length > 0 && (
                <>
                  <Box
                    sx={{
                      px: 2, pt: 2, pb: 0.5,
                      display: "flex", alignItems: "center",
                      cursor: "pointer", userSelect: "none",
                    }}
                    onClick={() => setShowAllChatio((v) => !v)}
                  >
                    <Typography variant="caption" fontWeight="bold" color="primary" sx={{ flex: 1, letterSpacing: 0.5 }}>
                      TODOS EN CHATIO · {filteredAll.length}
                    </Typography>
                    {showAllChatio
                      ? <ExpandLessIcon fontSize="small" sx={{ color: "#1976d2" }} />
                      : <ExpandMoreIcon fontSize="small" sx={{ color: "#1976d2" }} />}
                  </Box>
                  <Collapse in={showAllChatio}>
                    <List disablePadding>
                      {filteredAll.map((user, i) => (
                        <React.Fragment key={user.id}>
                          <ListItemButton
                            onClick={() => handleStartChat(user)}
                            sx={{ px: 2, py: 1.2, "&:hover": { bgcolor: "#f0f4ff" } }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                src={user.profileImageUrl || user.photoURL || ""}
                                sx={{ bgcolor: avatarColor(user.displayName), width: 46, height: 46 }}
                              >
                                {(user.displayName || "?")[0].toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="body1" fontWeight="500">
                                  {user.displayName || "Usuario"}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {user.email || ""}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                          {i < filteredAll.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Collapse>
                </>
              )}

              {/* Empty state */}
              {filteredAll.length === 0 && filteredMatched.length === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 10, gap: 2, px: 4 }}>
                  <PersonAddAlt1Icon sx={{ fontSize: 64, color: "#ccc" }} />
                  <Typography variant="body1" color="text.secondary" fontWeight="500" align="center">
                    {searchTerm
                      ? `Sin resultados para "${searchTerm}"`
                      : "Aún no hay otros usuarios registrados en Chatio."}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Slide>
  );
}

export default ContactSearch;
